import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { paymentService } from '../../services/paymentService';
import Header from '../../components/ui/Header';
import MobileBottomNav from '../../components/ui/MobileBottomNav';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';

const MemberPaymentMethods = () => {
  const navigate = useNavigate();
  const { user, currentTenant, loading: authLoading } = useAuth();
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showAddGymPayment, setShowAddGymPayment] = useState(false);
  const [addingGymPayment, setAddingGymPayment] = useState(false);

  useEffect(() => {
    if (!authLoading && !user) {
      navigate('/login');
    }
  }, [user, authLoading, navigate]);

  useEffect(() => {
    if (user?.id && currentTenant?.id) {
      loadPaymentMethods();
    }
  }, [user, currentTenant]);

  const loadPaymentMethods = async () => {
    setLoading(true);
    setError('');
    try {
      const methods = await paymentService?.getPaymentMethods(user?.id, currentTenant?.id);
      setPaymentMethods(methods);
    } catch (err) {
      setError(err?.message || 'Failed to load payment methods');
      console.error('Load payment methods error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSetDefault = async (methodId) => {
    try {
      await paymentService?.setDefaultPaymentMethod(methodId, user?.id, currentTenant?.id);
      setSuccess('Default payment method updated');
      loadPaymentMethods();
    } catch (err) {
      setError(err?.message || 'Failed to set default payment method');
      console.error('Set default error:', err);
    }
  };

  const handleDelete = async (methodId) => {
    if (!confirm('Are you sure you want to remove this payment method?')) return;

    try {
      await paymentService?.deletePaymentMethod(methodId);
      setSuccess('Payment method removed successfully');
      loadPaymentMethods();
    } catch (err) {
      setError(err?.message || 'Failed to remove payment method');
      console.error('Delete payment method error:', err);
    }
  };

  const handleAddGymPayment = async () => {
    setAddingGymPayment(true);
    setError('');
    try {
      const gymPaymentData = {
        tenant_id: currentTenant?.id,
        member_id: user?.id,
        processor_type: 'other',
        processor_payment_method_id: `gym_payment_${user?.id}_${Date.now()}`,
        payment_method_type: 'cash',
        is_default: paymentMethods?.length === 0,
        is_active: true
      };
      
      await paymentService?.savePaymentMethod(gymPaymentData);
      setSuccess('Pay at Gym payment method added successfully');
      setShowAddGymPayment(false);
      loadPaymentMethods();
    } catch (err) {
      setError(err?.message || 'Failed to add payment method');
      console.error('Add gym payment error:', err);
    } finally {
      setAddingGymPayment(false);
    }
  };

  const getCardIcon = (brand) => {
    const icons = {
      visa: 'CreditCard',
      mastercard: 'CreditCard',
      amex: 'CreditCard',
      discover: 'CreditCard'
    };
    return icons?.[brand?.toLowerCase()] || 'CreditCard';
  };

  const getCardColor = (brand) => {
    const colors = {
      visa: '#1A1F71',
      mastercard: '#EB001B',
      amex: '#006FCF',
      discover: '#FF6000'
    };
    return colors?.[brand?.toLowerCase()] || 'var(--color-primary)';
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <div className="flex items-center justify-center h-[calc(100vh-80px)]">
          <Icon name="Loader2" className="w-8 h-8 animate-spin text-primary" />
        </div>
        <MobileBottomNav />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Header />
      
      <main className="max-w-4xl mx-auto px-6 py-8 pb-24">
        <div className="mb-8">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate('/member-profile')}
            iconName="ArrowLeft"
            iconPosition="left"
            className="mb-4"
          >
            Back to Profile
          </Button>
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-foreground mb-2">Payment Methods</h1>
              <p className="text-muted-foreground">Manage your credit cards, PayPal, and gym payment options</p>
            </div>
            <div className="flex gap-2">
              <Button
                onClick={() => setShowAddGymPayment(true)}
                iconName="Building2"
                iconPosition="left"
                variant="outline"
              >
                Add Gym Payment
              </Button>
              <Button
                onClick={() => navigate('/member-profile')}
                iconName="Plus"
                iconPosition="left"
              >
                Add Card/PayPal
              </Button>
            </div>
          </div>
        </div>

        {error && (
          <div className="bg-destructive/10 border border-destructive/20 rounded-lg p-4 mb-6">
            <p className="text-destructive text-sm">{error}</p>
          </div>
        )}

        {success && (
          <div className="bg-success/10 border border-success/20 rounded-lg p-4 mb-6">
            <p className="text-success text-sm">{success}</p>
          </div>
        )}

        <div className="space-y-4">
          {paymentMethods?.map((method) => (
            <div
              key={method?.id}
              className={`bg-card rounded-xl shadow-md p-6 border-2 transition-smooth ${
                method?.is_default ? 'border-primary' : 'border-border'
              }`}
            >
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-4">
                  <div className="w-14 h-14 rounded-lg bg-gradient-to-br from-primary/20 to-primary/5 flex items-center justify-center">
                    <Icon
                      name={
                        method?.processor_type === 'paypal' ?'Wallet' 
                          : method?.payment_method_type === 'cash' ?'Building2'
                          : getCardIcon(method?.card_brand)
                      }
                      size={28}
                      color={
                        method?.processor_type === 'paypal' ?'#0070BA' 
                          : method?.payment_method_type === 'cash' ?'var(--color-primary)'
                          : getCardColor(method?.card_brand)
                      }
                    />
                  </div>
                  <div>
                    {method?.processor_type === 'paypal' ? (
                      <>
                        <h3 className="text-lg font-semibold text-foreground">PayPal</h3>
                        <p className="text-sm text-muted-foreground">{method?.paypal_email}</p>
                      </>
                    ) : method?.payment_method_type === 'cash' ? (
                      <>
                        <h3 className="text-lg font-semibold text-foreground">Pay at Gym</h3>
                        <p className="text-sm text-muted-foreground">In-person payment at gym location</p>
                      </>
                    ) : (
                      <>
                        <h3 className="text-lg font-semibold text-foreground capitalize">
                          {method?.card_brand} •••• {method?.card_last_four}
                        </h3>
                        <p className="text-sm text-muted-foreground">
                          Expires {method?.card_exp_month?.toString()?.padStart(2, '0')}/{method?.card_exp_year}
                        </p>
                      </>
                    )}
                  </div>
                </div>

                {method?.is_default && (
                  <div className="px-3 py-1 rounded-full bg-primary/10 text-primary text-xs font-medium">
                    Default
                  </div>
                )}
              </div>

              <div className="flex gap-2 mt-4">
                {!method?.is_default && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleSetDefault(method?.id)}
                    fullWidth
                  >
                    Set as Default
                  </Button>
                )}
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => handleDelete(method?.id)}
                  className="text-destructive hover:text-destructive"
                >
                  <Icon name="Trash2" size={16} />
                </Button>
              </div>
            </div>
          ))}

          {paymentMethods?.length === 0 && (
            <div className="bg-card rounded-xl shadow-md p-12 text-center">
              <Icon name="CreditCard" size={64} className="text-muted-foreground mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-foreground mb-2">No Payment Methods</h3>
              <p className="text-muted-foreground mb-6">
                Add a credit card, PayPal account, or set up gym payment
              </p>
              <div className="flex gap-3 justify-center">
                <Button onClick={() => setShowAddGymPayment(true)} iconName="Building2" iconPosition="left" variant="outline">
                  Pay at Gym
                </Button>
                <Button onClick={() => navigate('/member-profile')} iconName="Plus" iconPosition="left">
                  Add Card/PayPal
                </Button>
              </div>
            </div>
          )}
        </div>

        {showAddGymPayment && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-card rounded-xl shadow-2xl max-w-md w-full p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-xl font-bold text-foreground">Add Pay at Gym</h3>
                <button
                  onClick={() => setShowAddGymPayment(false)}
                  className="text-muted-foreground hover:text-foreground"
                  disabled={addingGymPayment}
                >
                  <Icon name="X" size={24} />
                </button>
              </div>

              <div className="space-y-4">
                <div className="p-4 bg-primary/5 border border-primary/20 rounded-lg">
                  <div className="flex items-start gap-3">
                    <Icon name="Building2" size={24} className="text-primary flex-shrink-0" />
                    <div>
                      <h4 className="font-semibold text-foreground mb-1">In-Person Payment</h4>
                      <p className="text-sm text-muted-foreground">
                        This option allows you to pay for your membership and services directly at the gym. Visit our front desk during business hours with a valid payment method.
                      </p>
                    </div>
                  </div>
                </div>

                <div className="p-4 bg-muted/30 rounded-lg">
                  <h4 className="font-semibold text-foreground mb-2">Accepted Payment Types:</h4>
                  <ul className="space-y-1 text-sm text-muted-foreground">
                    <li className="flex items-center gap-2">
                      <Icon name="Check" size={16} className="text-success" />
                      Cash
                    </li>
                    <li className="flex items-center gap-2">
                      <Icon name="Check" size={16} className="text-success" />
                      Credit/Debit Card
                    </li>
                    <li className="flex items-center gap-2">
                      <Icon name="Check" size={16} className="text-success" />
                      Check
                    </li>
                  </ul>
                </div>

                <div className="flex gap-3">
                  <Button
                    variant="outline"
                    onClick={() => setShowAddGymPayment(false)}
                    fullWidth
                    disabled={addingGymPayment}
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={handleAddGymPayment}
                    fullWidth
                    disabled={addingGymPayment}
                    iconName={addingGymPayment ? 'Loader2' : 'Check'}
                    iconPosition="left"
                  >
                    {addingGymPayment ? 'Adding...' : 'Add Payment Method'}
                  </Button>
                </div>
              </div>
            </div>
          </div>
        )}

        <div className="mt-8 bg-primary/5 border border-primary/20 rounded-lg p-6">
          <div className="flex items-start gap-3">
            <Icon name="Shield" size={24} className="text-primary flex-shrink-0 mt-0.5" />
            <div>
              <h3 className="font-semibold text-foreground mb-2">Your payment information is secure</h3>
              <p className="text-sm text-muted-foreground">
                We use industry-standard encryption to protect your payment details. Your card information is never stored on our servers and is processed securely through Stripe or PayPal.
              </p>
            </div>
          </div>
        </div>
      </main>

      <MobileBottomNav />
    </div>
  );
};

export default MemberPaymentMethods;
